import { z } from 'zod'

// Admin RBAC role. This is the only RBAC signal the front receives: a
// super_admin has siteId === null and sees every site; a site_admin is
// confined to its own. Site scoping is otherwise invisible (server-side).
export const ADMIN_ROLES = ['super_admin', 'site_admin'] as const
export type AdminRole = (typeof ADMIN_ROLES)[number]
export const zAdminRole = z.enum(ADMIN_ROLES)

// Site lifecycle.
export const SITE_STATUSES = ['active', 'paused'] as const
export type SiteStatus = (typeof SITE_STATUSES)[number]
export const zSiteStatus = z.enum(SITE_STATUSES)

// Google Ads account connection state. `auth_failed` means the refresh token
// died server-side — the fix is to run the OAuth connect flow again.
export const ADS_ACCOUNT_STATUSES = ['connected', 'auth_failed', 'disconnected'] as const
export type AdsAccountStatus = (typeof ADS_ACCOUNT_STATUSES)[number]
export const zAdsAccountStatus = z.enum(ADS_ACCOUNT_STATUSES)

// Autopilot mode per binding. `full_auto_prudent` auto-applies structural
// mutations; Smart Bidding still gates on allowBiddingAuto + conversion volume.
export const BINDING_MODES = ['suggest', 'supervised', 'full_auto_prudent'] as const
export type BindingMode = (typeof BINDING_MODES)[number]
export const zBindingMode = z.enum(BINDING_MODES)

export const BINDING_GOALS = ['traffic', 'leads', 'purchase'] as const
export type BindingGoal = (typeof BINDING_GOALS)[number]
export const zBindingGoal = z.enum(BINDING_GOALS)

// Change-proposal lifecycle (autopilot inbox). Only `proposed` (and
// `auto_eligible`) await a human decision; `approved` is picked up by the
// apply pipeline (CAS) which moves it through applying → applied/failed.
export const PROPOSAL_STATUSES = [
  'proposed',
  'auto_eligible',
  'approved',
  'applying',
  'applied',
  'failed',
  'rejected',
  'superseded',
] as const
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number]
export const zProposalStatus = z.enum(PROPOSAL_STATUSES)

// Proposal kinds documented by the API. The server may introduce new kinds;
// `kind` is typed as an open string, this list is what the UI knows how to
// label — unknown kinds render verbatim with a neutral treatment.
export const PROPOSAL_KINDS = [
  'add_negative_keyword',
  'pause_keyword',
  'pause_ad_group',
  'update_budget',
  'update_final_url',
  'update_bidding_strategy',
  'add_sitelink',
  'add_callout',
] as const
export type ProposalKind = (typeof PROPOSAL_KINDS)[number]

// Audit grades (campaign health "weather report").
export const AUDIT_GRADES = ['A', 'B', 'C', 'D', 'E', 'F'] as const
export type AuditGrade = (typeof AUDIT_GRADES)[number]
